import ErrorIcon from '@mui/icons-material/Error';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { useWindowWidth } from '@react-hook/window-size';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { API } from 'aws-amplify';
import React from 'react';
import { CardIcon } from '../../components/card-icon';
import {
  CustomerData,
  EventRegistrationData,
  EventTicketData,
  PYMNT_DFLT,
  TicketData,
} from '../../types';
import { captureError } from '../../utils/capture-error';
import { cardExpired } from '../../utils/card-expired';
import { toCurrency } from '../../utils/formatter';
import { RegisterConfirmation } from './register-confirmation';
import { RegisterPaymentAdd } from './register-payment-add';
import { RegisterSelections } from './register-selections';
import { RegisterTermsAccept } from './register-terms-accept';
import { RegisterTicketSelect } from './register-ticket-select';
import { SummaryPaymentSchedule } from './summary-payment-schedule';
import {
  checkAvailability,
  checkPaymentPlan,
  getSelectionStatus,
  RegisterAgentData,
} from './utils';
import { getErrorMessage } from '../../utils/error-message';

export function RegisterAgent(props: {
  data: EventRegistrationData;
  user: CognitoUser;
}) {
  // State
  const [activeStep, setActiveStep] = React.useState(0);
  const [register, setRegister] = React.useState<RegisterAgentData>({
    customerPaymentProfileId: '',
    TicketTypeId: 0,
    ticketCount: '1',
    totalPay: '0',
  });
  const [billing, setBilling] = React.useState<CustomerData>();
  const [shirtSizes, setShirtSizes] = React.useState<{ [key: number]: string }>(
    {},
  );
  const [mealTypes, setMealTypes] = React.useState<{ [key: number]: string }>(
    {},
  );
  const [ticket, setTicket] = React.useState<EventTicketData>();
  const [hasAgreed, setHasAgreed] = React.useState(false);
  // Hooks
  const width = useWindowWidth();

  const handleScroll = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Query - Agent Payment Info
  const path = '/events/payments/customer-profile';
  const query = useQuery({
    queryKey: [path],
    queryFn: async () => {
      const response: {
        data: CustomerData;
      } = await API.post('EventsAPI', path, {});

      return response.data;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  React.useEffect(() => {
    if (query.data) {
      setBilling(query.data);

      if (query.data?.customerPaymentProfiles.length) {
        const { customerPaymentProfiles } = query.data;
        // Check to to see if the user has set a default payment profile
        const defaultProfile = customerPaymentProfiles.find(
          (i) => i.defaultFor?.includes(PYMNT_DFLT['EVT']),
        );
        if (defaultProfile !== undefined) {
          setRegister((currentState) => ({
            ...currentState,
            customerPaymentProfileId:
              defaultProfile.customerPaymentProfileId || '',
          }));
        } else {
          // The user has not set a default profile so just use their first payment profile
          const { customerPaymentProfileId } = customerPaymentProfiles[0];
          setRegister((currentState) => ({
            ...currentState,
            customerPaymentProfileId: customerPaymentProfileId || '',
          }));
        }
      }
    }
  }, [query.data]);

  // Mutation - Purchase Tickets
  const mutation = useMutation({
    mutationFn: async () => {
      const response: {
        data: TicketData[];
      } = await API.post('EventsAPI', '/events/tickets/purchase', {
        body: {
          ...register,
          EventId: props.data.EventId,
          ShirtSizes: Object.values(shirtSizes),
          MealTypes: Object.values(mealTypes),
        },
      });

      return response.data;
    },
    onSuccess: () => {
      handleScroll();
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const ticketIsFree: boolean = Boolean(
    ticket &&
      ticket.MinPayPerTicket !== null &&
      ticket.MinPayPerTicket === 0 &&
      ticket.Price !== null &&
      ticket.Price === 0,
  );

  // Agents have the option to purchase multiple tickets
  // Check if multiple tickets are available to be purchased
  const { defaultLimit, startLimit } = checkAvailability({
    data: props.data,
    ticket,
  });

  // Agents have the option to purchase tickets on a payment plan
  // Check what those payment intervals would be
  const {
    payTodayMax,
    payTodayMin,
    payTodayBalance,
    hasToPayInFull,
    intervalsToEvent,
    displayIntervals,
    amountPerInterval,
  } = checkPaymentPlan({
    register,
    ticket,
    DateBegin: props.data.DateBegin,
  });

  // Checks to see if the user has selected a ticket type that
  // needs additional selections
  const { needsToSelectShirts, needsToSelectMeals } = getSelectionStatus({
    ticket,
    shirtSizes,
    mealTypes,
    ticketCount: register.ticketCount,
  });

  const isDesktop = width > 1024;

  // Purchase Error Message
  let purchaseErrorMsg = '';
  // @ts-ignore
  const errData = mutation.error?.response?.data;
  if (errData?.status === 'invalid') {
    if (errData?.errors?.declined) {
      purchaseErrorMsg = errData.errors.declined;
    } else if (errData?.errors?.ticketCount) {
      purchaseErrorMsg = errData?.errors?.ticketCount[0];
    } else if (errData?.error?.includes('400')) {
      purchaseErrorMsg = 'The transaction was unsuccessful.';
    }
  }

  // Submit button for ticket purchase
  let submitDisabled: boolean = true;
  let submitText: string = 'Submit';
  if (ticketIsFree) {
    submitDisabled =
      mutation.isLoading ||
      mutation.isError ||
      needsToSelectShirts ||
      needsToSelectMeals;
    submitText = mutation.isLoading
      ? 'Registering...'
      : `Register ${register.ticketCount} ticket(s)`;
  } else {
    submitDisabled =
      mutation.isLoading ||
      mutation.isError ||
      needsToSelectShirts ||
      needsToSelectMeals ||
      !register.customerPaymentProfileId ||
      Number(register.totalPay) < payTodayMin ||
      Number(register.totalPay) > payTodayMax;
    submitText = mutation.isLoading
      ? 'Purchasing...'
      : `Purchase and Pay ${toCurrency({
          value: Number(register.totalPay),
        })} today`;
  }

  const steps: { label: string; content: (index: number) => JSX.Element }[] = [
    {
      label: 'Select Ticket',
      content: (index) => {
        const nextDisabled: boolean =
          !register.TicketTypeId || register.ticketCount === '0';

        return (
          <div>
            <RegisterTicketSelect
              data={props.data}
              ticket={ticket}
              onSelect={(newTicket) => {
                handleScroll();
                if (newTicket) {
                  const { Price, TicketTypeId } = newTicket;
                  // Update selected ticket and amount to pay
                  setRegister((currentState) => {
                    const totalPay = Price * Number(currentState.ticketCount);
                    return {
                      ...currentState,
                      TicketTypeId,
                      totalPay: totalPay.toString(),
                    };
                  });
                  setTicket(newTicket);
                } else {
                  setRegister((currentState) => {
                    return {
                      ...currentState,
                      TicketTypeId: 0,
                      totalPay: '0',
                    };
                  });
                  setTicket(undefined);
                }
              }}
            />

            <Typography variant="h6" component="div" gutterBottom>
              Number of Tickets
            </Typography>

            <div style={{ width: '100%', maxWidth: 480 }}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ticket-count-label">Tickets</InputLabel>
                <Select
                  labelId="ticket-count-label"
                  id="ticket-count"
                  label="Tickets"
                  value={register.ticketCount}
                  onChange={(event) => {
                    const ticketCount = event.target.value as string;
                    const totalPay = ticket
                      ? ticket.Price * Number(ticketCount)
                      : 0;
                    setRegister((currentState) => ({
                      ...currentState,
                      ticketCount,
                      totalPay: totalPay.toString(),
                    }));
                  }}
                >
                  {Array.from({ length: defaultLimit }).map((_, index) => {
                    return (
                      <MenuItem key={index} value={(index + 1).toString()}>
                        {index + 1}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Stack spacing={1} sx={{ textAlign: 'center' }}>
                <small>
                  You can assign your tickets to attendees after you've
                  purchased the tickets.
                </small>

                {ticket && ticket.Price > 0 && hasToPayInFull ? (
                  <small>
                    {ticket.Title} tickets must be paid in full at time of
                    purchase.
                  </small>
                ) : ticket && intervalsToEvent > 0 ? (
                  <small>
                    Minimum of {toCurrency({ value: ticket.MinPayPerTicket })}{' '}
                    per ticket due today.
                  </small>
                ) : null}

                {props.data.PerUserTicketLimit !== null &&
                props.data.PerUserTicketLimit < startLimit ? (
                  <small>
                    Users are limited to purchasing{' '}
                    {props.data.PerUserTicketLimit} ticket(s) for this event.
                  </small>
                ) : null}

                {props.data.PerUserTicketLimit !== null &&
                props.data.TicketsPurchasedByUser !== null &&
                props.data.PerUserTicketLimit -
                  props.data.TicketsPurchasedByUser <
                  startLimit ? (
                  <small>
                    You can purchase up to{' '}
                    {props.data.PerUserTicketLimit -
                      props.data.TicketsPurchasedByUser}{' '}
                    more ticket(s) for this event.
                  </small>
                ) : null}
              </Stack>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                color="primary"
                variant="contained"
                disabled={nextDisabled}
                onClick={() => {
                  setActiveStep(index + 1);
                  handleScroll();
                }}
              >
                Next
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      label: 'Review Terms',
      content: (index) => {
        const nextDisabled: boolean = !hasAgreed;

        return (
          <div>
            <RegisterTermsAccept
              data={props.data}
              accepted={hasAgreed}
              onChange={(checked) => setHasAgreed(checked)}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(index - 1);
                  handleScroll();
                }}
              >
                Back
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={nextDisabled}
                onClick={() => {
                  setActiveStep(index + 1);
                  handleScroll();
                }}
              >
                Next
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      label: 'Complete Purchase',
      content: (index) => {
        return (
          <div>
            <Stack spacing={1}>
              <Typography variant="h6" component="div" gutterBottom>
                Total
              </Typography>

              <div>
                {ticket ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ fontSize: 14 }}>
                      {register.ticketCount} x {ticket.Title} at{' '}
                      {toCurrency({ value: ticket.Price })}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        height: 1,
                        backgroundColor: '#00000033',
                        marginLeft: 10,
                        marginRight: 10,
                      }}
                    />

                    <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                      {toCurrency({ value: payTodayMax })}
                    </div>
                  </div>
                ) : null}
              </div>

              <RegisterSelections
                ticket={ticket}
                ticketCount={Number(register.ticketCount || 0)}
                shirtSizes={shirtSizes}
                onChangeShirt={(itemKey, newShirt) => {
                  setShirtSizes((currentState) => {
                    return {
                      ...currentState,
                      [itemKey]: newShirt,
                    };
                  });
                }}
                mealTypes={mealTypes}
                onChangeMeal={(itemKey, newMeal) => {
                  setMealTypes((currentState) => {
                    return {
                      ...currentState,
                      [itemKey]: newMeal,
                    };
                  });
                }}
              />

              {ticketIsFree ? null : (
                <React.Fragment>
                  <Typography variant="h6" component="div" gutterBottom>
                    Payment
                  </Typography>

                  <div>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            disabled={hasToPayInFull}
                          >
                            <InputLabel htmlFor="purchase-pay-today">
                              Pay Today
                            </InputLabel>
                            <OutlinedInput
                              id="purchase-pay-today"
                              label="Pay Today"
                              type="number"
                              value={register.totalPay}
                              onChange={(event) =>
                                setRegister((currentState) => ({
                                  ...currentState,
                                  totalPay: event.target.value as string,
                                }))
                              }
                              startAdornment={
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              }
                            />
                          </FormControl>

                          {!hasToPayInFull ? (
                            <React.Fragment>
                              <Stack
                                spacing={1}
                                direction="row"
                                alignItems="center"
                              >
                                <Button
                                  fullWidth
                                  disableElevation
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setRegister((currentState) => ({
                                      ...currentState,
                                      totalPay: payTodayMin.toString(),
                                    }))
                                  }
                                >
                                  Min
                                </Button>

                                <Button
                                  fullWidth
                                  disableElevation
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setRegister((currentState) => ({
                                      ...currentState,
                                      totalPay: (payTodayMax * 0.5).toString(),
                                    }))
                                  }
                                >
                                  Half
                                </Button>

                                <Button
                                  fullWidth
                                  disableElevation
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setRegister((currentState) => ({
                                      ...currentState,
                                      totalPay: payTodayMax.toString(),
                                    }))
                                  }
                                >
                                  Full
                                </Button>
                              </Stack>

                              {ticket ? (
                                <Box sx={{ textAlign: 'center', pb: 1 }}>
                                  <small>
                                    Minimum of{' '}
                                    {toCurrency({
                                      value: ticket.MinPayPerTicket,
                                    })}{' '}
                                    per ticket due today.
                                  </small>
                                </Box>
                              ) : null}
                            </React.Fragment>
                          ) : null}
                        </Stack>
                      </Grid>

                      {billing?.customerPaymentProfiles.length ? (
                        <Grid item xs={12} sm={6}>
                          <Stack spacing={1}>
                            <FormControl
                              fullWidth
                              variant="outlined"
                              size="small"
                            >
                              <InputLabel id="register-agent-card-label">
                                Card
                              </InputLabel>
                              <Select
                                labelId="register-agent-card-label"
                                id="register-agent-card"
                                label="Card"
                                value={register.customerPaymentProfileId}
                                onChange={(event) => {
                                  const profileId = event.target
                                    .value as string;
                                  setRegister((currentState) => ({
                                    ...currentState,
                                    customerPaymentProfileId: profileId,
                                  }));
                                }}
                              >
                                {billing?.customerPaymentProfiles.map(
                                  (profile) => {
                                    if (profile.customerPaymentProfileId) {
                                      const { expirationDate } = profile;
                                      return (
                                        <MenuItem
                                          key={profile.customerPaymentProfileId}
                                          value={
                                            profile.customerPaymentProfileId
                                          }
                                          sx={{ fontSize: 12 }}
                                        >
                                          <Box sx={{ width: '100%' }}>
                                            <Stack
                                              divider={
                                                <Box
                                                  sx={{
                                                    mx: 0.6,
                                                    height: 3,
                                                    width: 3,
                                                    borderRadius: 8,
                                                    backgroundColor: '#a1a1a1',
                                                  }}
                                                />
                                              }
                                              direction="row"
                                              alignItems="center"
                                              sx={{ width: '100%' }}
                                            >
                                              <CardIcon
                                                cardType={
                                                  profile.cardType || ''
                                                }
                                              />
                                              {profile.cardType}
                                              {profile.cardNumber}
                                              {expirationDate}
                                              {cardExpired({
                                                expirationDate,
                                              }) ? (
                                                <Chip
                                                  label="Expired"
                                                  variant="outlined"
                                                  color="secondary"
                                                  size="small"
                                                  icon={<ErrorIcon />}
                                                  sx={{ fontWeight: 'bold' }}
                                                />
                                              ) : null}
                                            </Stack>
                                          </Box>
                                        </MenuItem>
                                      );
                                    } else {
                                      return null;
                                    }
                                  },
                                )}
                              </Select>
                            </FormControl>

                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <RegisterPaymentAdd
                                isAgent
                                onUpdateBilling={(newBilling) => {
                                  setBilling(newBilling);
                                }}
                              />
                            </div>
                          </Stack>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={6}>
                          <RegisterPaymentAdd
                            isAgent
                            buttonLarge
                            buttonText="Add Payment Information"
                            onUpdateBilling={(newBilling) => {
                              setBilling(newBilling);
                            }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        {displayIntervals ? (
                          <p style={{ textAlign: 'right', fontSize: 14 }}>
                            On the <strong>1st</strong> of each month you will
                            be charged{' '}
                            <strong>
                              {toCurrency({ value: amountPerInterval })}
                            </strong>{' '}
                            for the remaining{' '}
                            <strong>
                              {toCurrency({ value: payTodayBalance })}
                            </strong>{' '}
                            ticket balance.
                          </p>
                        ) : null}

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <SummaryPaymentSchedule
                            data={props.data}
                            payToday={Number(register.totalPay)}
                            displayIntervals={displayIntervals}
                            intervalsToEvent={intervalsToEvent}
                            amountPerInterval={amountPerInterval}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </React.Fragment>
              )}

              {mutation.isError ? (
                <Alert severity="error" onClose={() => mutation.reset()}>
                  <AlertTitle>An error has occurred</AlertTitle>
                  {purchaseErrorMsg ||
                    getErrorMessage({
                      error: mutation.error,
                      fallback: 'Please try again.',
                    })}
                </Alert>
              ) : null}

              <Stack
                spacing={1}
                direction="row"
                alignItems="flex-end"
                justifyContent="space-between"
              >
                <Button
                  disabled={mutation.isLoading}
                  variant="outlined"
                  onClick={() => {
                    setActiveStep(index - 1);
                    handleScroll();

                    // Reset shirt sizes and meal types
                    setShirtSizes({});
                    setMealTypes({});
                  }}
                >
                  Back
                </Button>

                <Button
                  color="secondary"
                  variant="contained"
                  size="large"
                  disabled={submitDisabled}
                  onClick={() => mutation.mutate()}
                  startIcon={
                    mutation.isLoading ? <CircularProgress size={20} /> : null
                  }
                >
                  {submitText}
                </Button>
              </Stack>
            </Stack>
          </div>
        );
      },
    },
  ];

  if (query.isLoading) {
    return <LinearProgress />;
  } else if (mutation.isSuccess) {
    return (
      <RegisterConfirmation
        data={props.data}
        billing={billing}
        paymentProfileId={register.customerPaymentProfileId}
        payToday={register.totalPay}
        ticketData={ticket}
        ticketCount={register.ticketCount}
        ticketsPurchased={mutation.data}
      />
    );
  } else {
    return (
      <div style={{ backgroundColor: '#fff' }}>
        <Stepper
          activeStep={activeStep}
          orientation={isDesktop ? 'horizontal' : 'vertical'}
          sx={{ p: 1 }}
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              {isDesktop ? null : (
                <StepContent>{step.content(index)}</StepContent>
              )}
            </Step>
          ))}
        </Stepper>

        {isDesktop ? (
          <Box sx={{ backgroundColor: '#fff', p: 2 }}>
            {steps.map((step, index) => {
              if (activeStep === index) {
                return <div key={step.label}>{step.content(index)}</div>;
              } else {
                return null;
              }
            })}
          </Box>
        ) : null}
      </div>
    );
  }
}
